import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import GoogleButton from "@/auth/GoogleButton";
import { ArrowRight, Building2, Globe, Users } from "lucide-react";
import { toast } from "sonner";

const INDUSTRIES = ["horeca","retail","zorg","kantoor","transport","onderwijs","other"];
const SIZES = ["1-10","11-50","51-200","201-500","500+"];
// For now Rostr onboards Nederland en België — verrekening in EUR, tijdzones
// zijn hier vastgeklikt zodat "size" en "trial" op de juiste dag beginnen.
const COUNTRIES = [
  { code: "NL", tz: "Europe/Amsterdam" },
  { code: "BE", tz: "Europe/Brussels"  },
];
const LANGS = ["nl","en"];

export default function CreateCompany() {
  const { setUser } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    company_name: "", industry: "horeca", country: "NL",
    timezone: "Europe/Amsterdam", language: "nl", size: "11-50",
    first_name: "", last_name: "", email: "", phone: "", password: "",
  });

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/companies", f);
      // Fase 6.5C — session cookies (access/refresh/csrf) are set by the
      // backend on this endpoint. No client-side token persistence.
      setUser(data.user);
      toast.success(`${t("welcome_company")} — ${data.company.name}`);
      navigate("/welcome", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] relative overflow-hidden">
      <div className="hero-mesh" aria-hidden />
      <nav className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/login" className="font-display text-[24px] font-bold tracking-tight text-[hsl(var(--text-strong))]">Rostr</Link>
        <Link to="/login" className="text-sm text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">{t("already_account")}</Link>
      </nav>

      <div className="relative z-10 max-w-xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="eyebrow mb-3">{t("step_of").replace("{n}", step)}</div>
          <h1 className="font-display text-[36px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))]">
            {step === 1 ? t("tell_company") : t("your_account")}
          </h1>
          <p className="mt-3 text-[15px] text-[hsl(var(--text-muted))]">
            {step === 1 ? t("company_data_help") : t("owner_note")}
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-6 md:p-8 mt-8">
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("company_name")} className="md:col-span-2">
                <input data-testid="cc-name" value={f.company_name} onChange={(e)=>setF({...f,company_name:e.target.value})} className="input-field" placeholder="Café Bleeker" />
              </Field>
              <Field label={t("industry")}>
                <select value={f.industry} onChange={(e)=>setF({...f,industry:e.target.value})} className="input-field">
                  {INDUSTRIES.map((i)=>(<option key={i} value={i}>{i}</option>))}
                </select>
              </Field>
              <Field label={t("size")}>
                <select value={f.size} onChange={(e)=>setF({...f,size:e.target.value})} className="input-field">
                  {SIZES.map((s)=>(<option key={s} value={s}>{s} {t("size_employees")}</option>))}
                </select>
              </Field>
              <Field label={t("country")}>
                <select
                  value={f.country}
                  onChange={(e) => {
                    const code = e.target.value;
                    const c = COUNTRIES.find((x) => x.code === code) || COUNTRIES[0];
                    setF({ ...f, country: c.code, timezone: c.tz });
                  }}
                  data-testid="cc-country"
                  className="input-field"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code === "NL" ? "🇳🇱 Nederland" : "🇧🇪 België"}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("language")}>
                <select value={f.language} onChange={(e)=>setF({...f,language:e.target.value})} className="input-field">
                  {LANGS.map((l)=>(<option key={l} value={l}>{l.toUpperCase()}</option>))}
                </select>
              </Field>
              <div className="md:col-span-2 flex justify-end mt-2">
                <button
                  onClick={() => setStep(2)}
                  disabled={!f.company_name}
                  data-testid="cc-next"
                  className="btn btn-primary"
                >{t("next")} <ArrowRight size={14} /></button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("first_name")}>
                <input data-testid="cc-first" value={f.first_name} onChange={(e)=>setF({...f,first_name:e.target.value})} className="input-field" />
              </Field>
              <Field label={t("last_name")}>
                <input data-testid="cc-last" value={f.last_name} onChange={(e)=>setF({...f,last_name:e.target.value})} className="input-field" />
              </Field>
              <div className="md:col-span-2">
                <GoogleButton
                  intent="create_company"
                  dataTestid="cc-google"
                  disabled={!f.first_name || !f.last_name}
                  payload={{
                    company_name: f.company_name,
                    industry: f.industry,
                    country: f.country,
                    timezone: f.timezone,
                    language: f.language,
                    size: f.size,
                    first_name: f.first_name,
                    last_name: f.last_name,
                  }}
                />
                <div className="text-[11px] text-[hsl(var(--text-muted))] mt-2 text-center">
                  {t("fastest_option")}
                </div>
              </div>
              <div className="md:col-span-2 flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-muted))]">{t("or_email_password")}</span>
                <div className="flex-1 h-px bg-[hsl(var(--border))]" />
              </div>
              <Field label={t("email_addr")} className="md:col-span-2">
                <input type="email" data-testid="cc-email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className="input-field" placeholder="jij@bedrijf.nl" />
              </Field>
              <Field label={t("phone")}>
                <input data-testid="cc-phone" value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} className="input-field" placeholder="+31 6 12345678" />
              </Field>
              <Field label={t("password")}>
                <input type="password" data-testid="cc-password" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} className="input-field" placeholder={t("min_6_chars")} />
              </Field>
              {error && (
                <div className="md:col-span-2 text-sm text-[hsl(var(--danger))] bg-[hsl(var(--danger-soft))] px-3 py-2 rounded-xl">{error}</div>
              )}
              <div className="md:col-span-2 flex justify-between mt-2">
                <button onClick={()=>setStep(1)} className="btn btn-quiet">{t("back")}</button>
                <button
                  onClick={submit} disabled={busy || !f.email || !f.password || !f.first_name}
                  data-testid="cc-submit" className="btn btn-primary"
                >{busy ? t("working") : t("create_company")} <ArrowRight size={14} /></button>
              </div>
              <div className="md:col-span-2 text-[12px] text-[hsl(var(--text-muted))] mt-2 flex items-center gap-2">
                <Globe size={12}/> {t("no_credit_card")}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children, className="" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{label}</span>
      {children}
    </label>
  );
}
